/* eslint-disable react-hooks/rules-of-hooks */
import { Permission } from '@idearoom/types';
import { OnlyExistingKeys } from '../types/Repo';
import { useAppSelector } from '../hooks';
import { unknownGroup } from '../constants/Group';
import { isIdeaRoomGroup, isIdeaRoomUser } from '../utils/userUtils';
import { LeadOrOrder } from '../types/LeadOrOrder';
import { useGetLeadsQuery, useGetOrdersQuery } from '../services/leadApi';
import { getRequestHeader } from '../utils/apiUtils';
import { MUIDataGridKey } from '../constants/MUIDataGrid';
import { OrderOwner } from '../types/OrderOwner';
import { SystemGroups } from '../constants/SystemGroups';
import { useGetGroupQuery } from '../services/groupApi';
import { unknownUser } from '../types/User';
import { hasAdminPermissions } from '../utils/permissionUtils';
import { OrderStatusId } from '../constants/OrderStatus';
import { AppState } from '../types/AppState';

const EMPTY_ORDERS_REFERENCE: Readonly<LeadOrOrder[]> = [];
const EMPTY_LEADS_REFERENCE: Readonly<LeadOrOrder[]> = [];

type UseOrderLeadRepoReturn<UseOrdersType, UseLeadsType, UseOrderOwnerType> = OnlyExistingKeys<{
  orders: UseOrdersType extends true ? LeadOrOrder[] : never;
  isLoadingOrders: UseOrdersType extends true ? boolean : never;
  leads: UseLeadsType extends true ? LeadOrOrder[] : never;
  isLoadingLeads: UseLeadsType extends true ? boolean : never;
  orderOwnerOptions: UseOrderOwnerType extends true ? OrderOwner[] : never;
  isLoadingOrderOwnerOptions: UseOrderOwnerType extends true ? boolean : never;
}>;

export const useOrderLeadRepo = <
  UseOrdersType extends boolean = false,
  UseLeadsType extends boolean = false,
  UseOrderOwnerType extends boolean = false,
>({
  useOrders,
  useLeads,
  useOrderOwner,
}: {
  useOrders?: UseOrdersType;
  useLeads?: UseLeadsType;
  useOrderOwner?: UseOrderOwnerType;
} = {}): UseOrderLeadRepoReturn<UseOrdersType, UseLeadsType, UseOrderOwnerType> => {
  const { groupId = '', configurators: groupConfigurators = [] } = useAppSelector(
    (state: AppState) => state.currentUser.group || unknownGroup,
  );
  const { user: currentUser = unknownUser } = useAppSelector((state: AppState) => state.currentUser);
  const { systemGroupOrderConfigurators = [] } = useAppSelector(
    (state: AppState) => state.systemGroupOrderConfigurators,
  );
  const { dateRangeFilter: { startDate: orderStartDate = undefined, endDate: orderEndDate = undefined } = {} } =
    useAppSelector(
      (state: AppState) => state.muiDataGrid[MUIDataGridKey.Orders] || state.muiDataGrid[MUIDataGridKey.Default],
    );
  const { dateRangeFilter: { startDate: leadStartDate = undefined, endDate: leadEndDate = undefined } = {} } =
    useAppSelector(
      (state: AppState) => state.muiDataGrid[MUIDataGridKey.Leads] || state.muiDataGrid[MUIDataGridKey.Default],
    );
  const dealers = useAppSelector((state: AppState) => state.currentUser.availableDealers || []);

  const result: any = {};
  if (useOrders) {
    const configurators = (
      isIdeaRoomGroup(groupId)
        ? systemGroupOrderConfigurators
        : groupConfigurators.map((c) => ({ configurator: c.key, vendor: c.vendor }))
    )
      .map((c) => `${c.configurator}-${c.vendor}`)
      .join(',');
    const orderRequest = {
      options: {
        queryParams: {
          configurators,
          startDate: orderStartDate
            ? (typeof orderStartDate === 'string' ? new Date(orderStartDate) : orderStartDate).toISOString()
            : undefined,
          endDate: orderEndDate
            ? (typeof orderEndDate === 'string' ? new Date(orderEndDate) : orderEndDate).toISOString()
            : undefined,
        },
        headers: getRequestHeader({ groupId }),
      },
    };

    const skip = !configurators.length;
    const { data: { orders = EMPTY_ORDERS_REFERENCE } = {}, isFetching: isLoadingOrders } = useGetOrdersQuery(
      { orderRequest },
      {
        skip,
        refetchOnMountOrArgChange: true,
      },
    );
    result.orders = orders;
    result.isLoadingOrders = isLoadingOrders;
  }

  if (useLeads) {
    const configurators = (
      isIdeaRoomGroup(groupId)
        ? systemGroupOrderConfigurators
        : groupConfigurators.map((c) => ({ configurator: c.key, vendor: c.vendor }))
    )
      .map((c) => `${c.configurator}-${c.vendor}`)
      .join(',');
    const leadRequest = {
      options: {
        queryParams: {
          configurators,
          startDate: leadStartDate
            ? (typeof leadStartDate === 'string' ? new Date(leadStartDate) : leadStartDate).toISOString()
            : undefined,
          endDate: leadEndDate
            ? (typeof leadEndDate === 'string' ? new Date(leadEndDate) : leadEndDate).toISOString()
            : undefined,
        },
        headers: getRequestHeader({ groupId }),
      },
    };

    const skip = !configurators.length;
    const { data: { orders: leads = EMPTY_LEADS_REFERENCE } = {}, isFetching: isLoadingLeads } = useGetLeadsQuery(
      { leadRequest },
      {
        skip,
        refetchOnMountOrArgChange: 60,
      },
    );

    let filteredLeads = leads as LeadOrOrder[];
    // Get the current user to filter on permissions and dealer if necessary
    const currentUserDealers = currentUser.dealers
      ? currentUser.dealers.filter((d) => d !== null && d !== undefined)
      : [];

    // Filter leads for SALES users to be only the leads owned by them
    if (
      currentUser.permissions.length === 0 ||
      (currentUser.permissions.length === 1 && currentUser.permissions[0] === Permission.Sales)
    ) {
      filteredLeads = filteredLeads.filter((lead) => {
        const orderOwner = lead.ownerEmail ? lead.ownerEmail : lead.salesRepEmail;
        return orderOwner ? orderOwner.toLowerCase() === currentUser.email.toLowerCase() : false;
      });
    } else if (currentUserDealers.length > 0 && !hasAdminPermissions(currentUser.permissions)) {
      const availableDealers = dealers.filter((dealer) => currentUserDealers.includes(dealer.id || 0));
      filteredLeads = filteredLeads.filter((lead) =>
        lead.orderDealerKey || lead.dealerKey
          ? availableDealers.some((dealer) => dealer.key === (lead.orderDealerKey || lead.dealerKey))
          : false,
      );
    } else if (
      currentUser.permissions.includes(Permission.Manager) &&
      !hasAdminPermissions(currentUser.permissions) &&
      !currentUser.permissions.includes(Permission.SuperUser)
    ) {
      // If a manager does not have any dealers assigned, only show them unassigned leads
      filteredLeads = filteredLeads.filter((lead) => !(lead.orderDealerKey || lead.dealerKey));
    }

    // If not an Idearoom admin user, filter out any "deleted" leads
    if (!isIdeaRoomUser(currentUser)) {
      filteredLeads = filteredLeads.filter(
        (lead) => (lead.orderStatusId || OrderStatusId.NewLead) !== OrderStatusId.Deleted,
      );
    }

    result.leads = filteredLeads;
    result.isLoadingLeads = isLoadingLeads;
  }

  if (useOrderOwner) {
    const orderOwnerOptions: OrderOwner[] = [];

    // when logged in as an IDEA_ROOM user do not get options because they
    // wouldn't have the same group context
    if (groupId !== SystemGroups.IdeaRoom) {
      const { data: { group = unknownGroup } = {} } = useGetGroupQuery(
        { groupId },
        {
          skip: groupId === SystemGroups.IdeaRoom,
          refetchOnMountOrArgChange: true,
        },
      );

      if (group.members) {
        orderOwnerOptions.push(
          ...group.members
            .filter((member) => !isIdeaRoomUser(member) && member.permissions.includes(Permission.Sales))
            .map(({ email, name }) => ({ email, name })),
        );
      }
    }

    result.orderOwnerOptions = orderOwnerOptions;
    result.isLoadingOrderOwnerOptions = false;
  }

  return result;
};
